import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { ERROR_NOTIFICATION_COLOR, SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import panic from 'errors/panic';
import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import {
  IExtendedFormInput,
  useFillOutRevisionDataProvider,
} from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { useFillOutRevisionSaveDraftProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionSaveDraftProvider';
import { FORM_PAGE_PATH, REVISION_PAGE_PATH } from 'routes/paths';
import { useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mantine/core';
import { FormErrors } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import usePostSubmitActions from 'components/forms/revision/fill-out/data/hooks/use-post-submit-actions';
import useApproveAction from 'components/tables/data/revision/actions/hooks/use-approve-action';
import useConfirmAction from 'components/tables/data/revision/actions/hooks/use-confirm-action';
import { useRevisionLockProvider } from 'components/forms/revision/fill-out/data/RevisionLockProvider';

interface IFillOutRevisionSubmitProviderContext {
  submit: (options?: { notify?: boolean }) => void;
  loading: boolean;
  canImmediatelyApprove: boolean;
  canImmediatelyConfirm: boolean;
  approveAfterSubmit: boolean;
  confirmAfterSubmit: boolean;
  setApproveAfterSubmit: (value: boolean) => void;
  setConfirmAfterSubmit: (value: boolean) => void;
}

const FillOutRevisionSubmitProviderContext = createContext<IFillOutRevisionSubmitProviderContext>(undefined!);

/**
 * Provides the revision and form spec to the children.
 */
export function FillOutRevisionSubmitProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { revision, form, findInputByName } = useFillOutRevisionDataProvider();
  const { saveDraft } = useFillOutRevisionSaveDraftProvider();
  const { lockToken } = useRevisionLockProvider();
  const { getAction } = useApi();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(false);

  const { canImmediatelyApprove, canImmediatelyConfirm } = usePostSubmitActions(revision);
  const { approveRevision } = useApproveAction();
  const { confirmRevision } = useConfirmAction();

  const [approveAfterSubmit, setApproveAfterSubmit] = useState(true);
  const [confirmAfterSubmit, setConfirmAfterSubmit] = useState(true);

  const revisionDetailPath = useMemo(
    () =>
      revision.kind === 'revision'
        ? REVISION_PAGE_PATH.insert({ revisionId: revision.revisionId })
        : FORM_PAGE_PATH.insert({ revisionId: revision.revisionId }),
    [revision.kind, revision.revisionId]
  );

  /** Sends the revision data to the server. */
  const sendRequest = async () => {
    const submit = getAction('RevisionTechnicianSubmit');

    await saveDraft({ notify: false });
    await submit({
      parameters: { revisionId: String(revision.revisionId) },
      payload: { lockToken },
    });

    showNotification({
      title: revision.kind === 'revision' ? 'Revízna správa bola odovzdaná' : 'Formulár bol odovzdaný',
      message:
        revision.kind === 'revision'
          ? `Revíznu správu ${revision?.revisionName} ste úspešne odovzdali.`
          : `Formulár ${revision?.revisionName} ste úspešne odovzdali.`,
      color: SUCCESS_NOTIFICATION_COLOR,
    });

    const revisionMeta = { revisionId: revision.revisionId, revisionName: revision.revisionName, kind: revision.kind };

    if (canImmediatelyApprove && approveAfterSubmit) {
      await approveRevision({ revision: revisionMeta });
    }

    if (canImmediatelyApprove && approveAfterSubmit && canImmediatelyConfirm && confirmAfterSubmit) {
      await confirmRevision({ revision: revisionMeta });
    }

    navigate(revisionDetailPath);
  };

  /** Handles form errors. */
  const handleErrors = (errors: FormErrors) => {
    const missingFields: Record<string, IExtendedFormInput> = {};

    for (const nameWithPrefix in errors) {
      const name = nameWithPrefix.replace(/^fields\./, '').replace(/^devices\.\d+\.fields\./, '');
      const input = findInputByName(name);

      if (input && !missingFields[input.id]) {
        missingFields[input.id] = input;
      }
    }

    showNotification({
      title: 'Vo formulári sú chyby!',
      message: (
        <Stack spacing={8}>
          Vyplňte všetky povinné polia:
          <Stack spacing={4}>
            {Object.values(missingFields).map((field) => (
              <Box key={field.id}>
                - {field.spec.label} ({field.tabTitle})
              </Box>
            ))}
          </Stack>
        </Stack>
      ),
      color: ERROR_NOTIFICATION_COLOR,
    });
  };

  /** Sends the request if the form is valid. */
  const sendRequestIfValid = async () => {
    const { hasErrors, errors } = form.validate();

    if (hasErrors) {
      handleErrors(errors);
    } else {
      await sendRequest();
    }
  };

  /** Submits the revision data (with validation). */
  const submit = () => {
    if (!loading) {
      startLoading();
      sendRequestIfValid().catch(panic).finally(stopLoading);
    }
  };

  const value = useMemo(
    () => ({
      submit,
      loading,
      canImmediatelyApprove,
      canImmediatelyConfirm,
      approveAfterSubmit,
      confirmAfterSubmit,
      setApproveAfterSubmit,
      setConfirmAfterSubmit,
    }),
    [submit, loading, canImmediatelyApprove, canImmediatelyConfirm, approveAfterSubmit, confirmAfterSubmit]
  );

  return (
    <FillOutRevisionSubmitProviderContext.Provider value={value}>
      {children}
    </FillOutRevisionSubmitProviderContext.Provider>
  );
}

/**
 * Hook to get the revision and form spec.
 */
export function useFillOutRevisionSubmitProvider() {
  return useContext(FillOutRevisionSubmitProviderContext);
}
